"use client";

import { capturePostHogEvent } from "./posthog";
import { INVITE_SESSION_KEYS } from "./invite";

/* ============================================================================
   ASSESSMENT EVENT CATALOGUE — ported from v1 Assessment-React
   (src/utils/assessmentEvents.js).

   Event names and property names are v1's, character for character, INCLUDING
   its misspellings (`voilation_type`, `after_voilation`, `dropedoff`,
   `droppedoff`). They are the keys the existing PostHog insights and funnels
   are built on: correcting them here would silently split every chart in two
   the day v2 takes over a share of the traffic. Fix them in one migration
   across both apps, or not at all.

   What did change is how the properties are supplied. v1 threaded
   `assessmentAssignId, assessmentType, isCorporate, entityName` through every
   component as four positional arguments, which is why so many of its events
   carry `undefined` for them. Here they are read once from the session this tab
   holds — see sessionProps below.
   ========================================================================== */

type Properties = Record<string, unknown>;

function read(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null; // storage disabled — the event still goes, without the spine
  }
}

/**
 * The four properties v1 repeats on nearly every assessment event, taken from
 * the session the invite (or a signed-in student's exchange in
 * studentSession.ts) put in this tab.
 *
 * Deliberately NOT applied to the invite/OTP events below: those fire before a
 * session exists, and on a shared browser the only thing sessionStorage would
 * offer them is the PREVIOUS candidate's assignment.
 */
function sessionProps(): Properties {
  const company = read(INVITE_SESSION_KEYS.company);
  return {
    assessment_assign_id: read(INVITE_SESSION_KEYS.assessmentAssignedId),
    assessment_type: read(INVITE_SESSION_KEYS.assessmentType),
    // A corporate sitting is one invited against a company; an institute
    // student arrives through their college and carries no company.
    isCorporate: Boolean(company),
    entityName: company,
  };
}

/** Every assessment/practice/proctoring event carries the spine. */
function capture(eventName: string, properties: Properties = {}): void {
  capturePostHogEvent(eventName, { ...sessionProps(), ...properties });
}

/* -------------------------------------------------------------------------
   INVITE / OTP LOGIN
   The unauthenticated entry point (/?inviteToken=… → email → OTP → runner).
   Never put the invite token or the OTP code in a property — they are
   credentials.
   ---------------------------------------------------------------------- */

export function trackInviteLinkOpened(
  assessmentType: string | null,
  inviteStatus: "new" | "already_attempted",
  role: string | null,
  company: string | null,
): void {
  capturePostHogEvent("invite_link_opened", {
    assessment_type: assessmentType,
    invite_status: inviteStatus,
    role,
    company,
  });
}

export function trackInviteLinkInvalid(reason: string): void {
  capturePostHogEvent("invite_link_invalid", { reason });
}

export function trackInviteOtpRequested(
  assessmentType: string | null,
  isResend: boolean,
  role: string | null,
  company: string | null,
): void {
  capturePostHogEvent("invite_otp_requested", {
    assessment_type: assessmentType,
    is_resend: isResend,
    role,
    company,
  });
}

export function trackInviteOtpRequestFailed(
  assessmentType: string | null,
  isResend: boolean,
  reason: string,
): void {
  capturePostHogEvent("invite_otp_request_failed", {
    assessment_type: assessmentType,
    is_resend: isResend,
    reason,
  });
}

export function trackInviteOtpVerified(
  assessmentAssignId: string | null,
  assessmentType: string | null,
  role: string | null,
  company: string | null,
): void {
  capturePostHogEvent("invite_otp_verified", {
    assessment_assign_id: assessmentAssignId,
    assessment_type: assessmentType,
    role,
    company,
  });
}

export function trackInviteOtpVerificationFailed(assessmentType: string | null, reason: string): void {
  capturePostHogEvent("invite_otp_verification_failed", {
    assessment_type: assessmentType,
    reason,
  });
}

/* -------------------------------------------------------------------------
   ASSESSMENT LIFECYCLE
   ---------------------------------------------------------------------- */

export function trackAssessmentEntered(): void {
  capture("assessment_entered", { entered_time: Date.now() });
}

export function trackAssessmentStarted(): void {
  capture("assessment_started", { start_time: Date.now() });
}

export function trackAssessmentCompleted(totalTimeSeconds: number): void {
  capture("assessment_completed", { total_time_seconds: totalTimeSeconds });
}

export function trackAssessmentDroppedOff(timeElapsedSeconds: number): void {
  capture("assessment_droppedoff", { time_elapsed_seconds: timeElapsedSeconds });
}

export function trackAssessmentAutoSubmitted(violationCount: number, reason: string): void {
  capture("assessment_auto_submitted", {
    violation_count: violationCount,
    reason,
    auto_submit_time: Date.now(),
  });
}

/* -------------------------------------------------------------------------
   QUESTIONS
   ---------------------------------------------------------------------- */

export function trackQuestionViewed(
  questionId: string,
  questionType: string,
  questionNumber: number,
  totalQuestions: number,
): void {
  capture("assessment_question_viewed", {
    question_id: questionId,
    question_type: questionType,
    question_number: questionNumber,
    total_questions: totalQuestions,
  });
}

export function trackQuestionAnswered(
  questionId: string,
  questionType: string,
  answerValue: unknown,
  timeSpentSeconds: number,
): void {
  capture("assessment_question_answered", {
    question_id: questionId,
    question_type: questionType,
    answer_value: answerValue,
    time_spent_seconds: timeSpentSeconds,
  });
}

export function trackQuestionSkipped(
  questionId: string,
  questionType: string,
  questionNumber: number,
): void {
  capture("assessment_question_skipped", {
    question_id: questionId,
    question_type: questionType,
    question_number: questionNumber,
  });
}

export function trackAnswerChanged(
  questionId: string,
  oldAnswer: unknown,
  newAnswer: unknown,
  changeCount: number,
): void {
  capture("assessment_answer_changed", {
    question_id: questionId,
    old_answer: oldAnswer,
    new_answer: newAnswer,
    change_count: changeCount,
  });
}

/* -------------------------------------------------------------------------
   NAVIGATION
   ---------------------------------------------------------------------- */

export function trackNextQuestion(currentQuestion: string, nextQuestion: string): void {
  capture("assessment_next_question", {
    current_question: currentQuestion,
    next_question: nextQuestion,
  });
}

export function trackPreviousQuestion(currentQuestion: string, previousQuestion: string): void {
  capture("assessment_previous_question", {
    current_question: currentQuestion,
    previous_question: previousQuestion,
  });
}

export function trackQuestionNavigationUsed(fromQuestion: string, toQuestion: string): void {
  capture("assessment_question_navigation_used", {
    from_question: fromQuestion,
    to_question: toQuestion,
  });
}

export function trackSectionChanged(fromSection: string, toSection: string, sectionType: string): void {
  capture("assessment_section_changed", {
    from_section: fromSection,
    to_section: toSection,
    section_type: sectionType,
  });
}

/* -------------------------------------------------------------------------
   DIAGNOSIS
   v1's placement-prep journey. v2 does not serve it yet; these stay so the
   catalogue is one file across both apps and the screens can fire them the day
   they land, without a second migration of event names.
   ---------------------------------------------------------------------- */

export function trackDiagnosisStarted(diagnosisNumber: number): void {
  capture("assessment_diagnosis_started", {
    diagnosis_number: diagnosisNumber,
    started_time: Date.now(),
  });
}

export function trackDiagnosisCompleted(
  diagnosisNumber: number,
  score: number,
  timeTakenSeconds: number,
): void {
  capture("assessment_diagnosis_completed", {
    diagnosis_number: diagnosisNumber,
    score,
    time_taken_seconds: timeTakenSeconds,
  });
}

export function trackDiagnosisDroppedOff(diagnosisNumber: number, timeElapsedSeconds: number): void {
  capture("assessment_diagnosis_droppedoff", {
    diagnosis_number: diagnosisNumber,
    time_elapsed_seconds: timeElapsedSeconds,
  });
}

export function trackDiagnosisProgressUpdated(completedCount: number, requiredCount: number): void {
  capture("assessment_diagnosis_progress_updated", {
    completed_count: completedCount,
    required_count: requiredCount,
  });
}

/* -------------------------------------------------------------------------
   PRACTICE
   As above — v1's placement-prep surface, unwired in v2 until it has one.
   ---------------------------------------------------------------------- */

export function trackPracticeUnlocked(): void {
  // v1 sends Date.now().toString().split(" ")[0] here, which is the epoch
  // number, not a date — kept as a real timestamp so the property is usable.
  capture("assessment_practice_unlocked", { unlock_date: Date.now() });
}

export function trackPracticeTabViewed(practiceUnlocked: boolean, diagnosisCompleted: boolean): void {
  capture("assessment_practice_tab_viewed", {
    practice_unlocked: practiceUnlocked,
    diagnosis_completed: diagnosisCompleted,
  });
}

export function trackPracticeLockedClicked(diagnosisCompleted: boolean): void {
  capture("assessment_practice_locked_clicked", { diagnosis_completed: diagnosisCompleted });
}

export function trackPracticeSessionStarted(assessmentTopics: string[], mode: "AI" | "manual"): void {
  capture("assessment_practice_session_started", {
    assessment_topics: assessmentTopics,
    mode,
    started_time: Date.now(),
  });
}

export function trackPracticeQuestionAttempted(
  questionId: string,
  questionType: string,
  attemptNumber: number,
): void {
  capture("assessment_practice_question_attempted", {
    question_id: questionId,
    question_type: questionType,
    attempt_number: attemptNumber,
    started_time: Date.now(),
  });
}

export function trackPracticeAnswerSubmitted(
  questionId: string,
  answerCorrect: boolean,
  timeSpentSeconds: number,
): void {
  capture("assessment_practice_answer_submitted", {
    question_id: questionId,
    answer_correct: answerCorrect,
    time_spent_seconds: timeSpentSeconds,
  });
}

export function trackPracticeSessionCompleted(timeSpentSeconds: number): void {
  capture("assessment_practice_session_completed", { time_spent_seconds: timeSpentSeconds });
}

export function trackPracticeSessionDroppedOff(
  questionsAttempted: number,
  timeElapsedSeconds: number,
): void {
  capture("assessment_practice_session_dropedoff", {
    questions_attempted: questionsAttempted,
    time_elapsed_seconds: timeElapsedSeconds,
  });
}

/* -------------------------------------------------------------------------
   PROCTORING
   ---------------------------------------------------------------------- */

export function trackFullscreenEntered(afterViolation = false): void {
  capture("assessment_fullscreen_entered", {
    after_voilation: afterViolation, // v1 spelling — see header
    time: Date.now(),
  });
}

export function trackFullscreenExited(violationCount: number): void {
  capture("assessment_fullscreen_exited", {
    violation_count: violationCount,
    time: Date.now(),
  });
}

export function trackViolationDetected(violationCount: number, violationType: string): void {
  capture("assessment_violation_detected", {
    violation_count: violationCount,
    voilation_type: violationType, // v1 spelling — see header
    time: Date.now(),
  });
}

export function trackCopyPasteAttempted(content: string): void {
  capture("assessment_copy_paste_attempted", {
    content,
    time: Date.now(),
  });
}

export function trackRightClick(): void {
  capture("assessment_right_click");
}
